import React, { useEffect, useCallback, useMemo } from 'react';
import { Button, Icon, Popup, Grid } from 'semantic-ui-react';
import LanguageSelector from './RightSection/LanguageSelector';
import UserMenu from './RightSection/UserMenu';
import AuthLinks from './RightSection/AuthLinks';
import NotesToggle from './RightSection/NotesToggle';
import AIToggle from './RightSection/AIToggle';

const RightSection = React.memo(({
  show_ai_chat,
  user,
  userMenu,
  userMenuChange,
  zoomValue = 1,
  onZoomMenuChange,
  visible,
  setVisible,
  aiVisible,
  setAiVisible,
  action_name
}) => {
  const handleZoomIn = useCallback((e) => {
    if (typeof onZoomMenuChange !== 'function') return;
    const newValue = Math.min(5, zoomValue + 0.1);
    onZoomMenuChange(e, { value: newValue });
  }, [zoomValue, onZoomMenuChange]);

  const handleZoomOut = useCallback((e) => {
    if (typeof onZoomMenuChange !== 'function') return;
    const newValue = Math.max(0.1, zoomValue - 0.1);
    onZoomMenuChange(e, { value: newValue });
  }, [zoomValue, onZoomMenuChange]);

  const handleZoomReset = useCallback((e) => {
    if (typeof onZoomMenuChange !== 'function') return;
    onZoomMenuChange(e, { value: 1 });
  }, [onZoomMenuChange]);

  const handleNotesToggle = useCallback((e) => {
    if (aiVisible) {
      setAiVisible(false);
      setVisible(true);
    } else if (visible) {
      setVisible(false);
    } else {
      setVisible(true);
    }
  }, [visible, setVisible, aiVisible, setAiVisible]);

  const handleAIToggle = useCallback((e) => {
    if (visible) {
      setVisible(false);
    }
    setAiVisible(!aiVisible);
  }, [aiVisible, setAiVisible, visible, setVisible]);

  const buttons = useMemo(() => (
    <>
      <style>{`
        .right-section-btn {
          padding: 4px 8px !important;
          border-radius: 4px !important;
          background: transparent !important;
          color: #ffffff !important;
          height: 40px !important;
          display: flex !important;
          align-items: center !important;
        }

        .right-section-btn:not(.no-hover):hover {
          background-color: rgba(255, 255, 255, 0.1) !important;
        }

      `}</style>
      <Button.Group>
        <Button
          className="right-section-btn"
          onClick={handleZoomOut}
          size='mini'
        >
          <Icon name='zoom-out' />
        </Button>
        <Button
          className="right-section-btn no-hover"
          onClick={handleZoomReset}
          size='mini'
        >
          {Math.round(zoomValue * 100)}%
        </Button>
        <Button
          className="right-section-btn"
          onClick={handleZoomIn}
          size='mini'
        >
          <Icon name='zoom-in' />
        </Button>
      </Button.Group>
      {(action_name === 'show' || action_name === 'lab') && (
        <>
          <NotesToggle
            visible={visible}
            onToggle={handleNotesToggle}
            l={{ toggle_notes: 'Toggle Notes' }}
            aiVisible={aiVisible}
          />
          {show_ai_chat && <AIToggle
            visible={aiVisible}
            onToggle={handleAIToggle}
            l={{ toggle_ai: 'Toggle AI Assistant' }}
          />}
        </>
      )}
    </>
  ), [zoomValue, visible, aiVisible, action_name, handleZoomIn, handleZoomOut, handleZoomReset, handleNotesToggle, handleAIToggle]);

  const userMenuElement = useMemo(() => (
    user && (
      <UserMenu 
        key={user.id} 
        userMenu={userMenu} 
        user={user} 
        userMenuChange={userMenuChange}
      />
    )
  ), [user, userMenu, userMenuChange]);

  const authLinksElement = useMemo(() => (
    !user && <AuthLinks />
  ), [user]);

  return (
    <Grid.Row style={{       
      marginRight: '5px',
      position: 'relative',
      top: '8px'
    }}>
      <Grid.Column width={4} style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px', 
        justifyContent: 'flex-end',
        margin: 0
      }}>
        {buttons}
        <div style={{ 
          marginLeft: 'auto', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '2px',
          marginRight: 0,
          paddingRight: 0
        }}>
          {userMenuElement}
          {authLinksElement}
          <LanguageSelector 
            onLanguageChange={(language) => {
              window.open(language.link, '_self');
            }}
            currentLanguage={document.documentElement.lang || 'en'}
          />
        </div>
      </Grid.Column>
    </Grid.Row>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.user?.id === nextProps.user?.id &&
    prevProps.zoomValue === nextProps.zoomValue &&
    prevProps.visible === nextProps.visible &&
    prevProps.aiVisible === nextProps.aiVisible &&
    prevProps.action_name === nextProps.action_name &&
    JSON.stringify(prevProps.userMenu) === JSON.stringify(nextProps.userMenu)
  );
});

export default RightSection;